import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { Plus, Trash2, Pencil, Tag as TagIcon, AlertTriangle } from "lucide-react";

/**
 * v5.7 Addition 12 — Tags Management (Settings sub-tab).
 * v5.14 bug-fix pass:
 *   1. Delete is a real hard-delete (no more "deactivate")
 *   2. When linked to deals, confirm with deal-count before cascading
 *   3. Fast Forward + Interesting now appear here like any other tag
 *   4. Duplicate names blocked (backend-enforced with a clear inline error)
 *   5. Behaviours column + section removed — tags are just Name/Color/Description
 */
export default function TagsManagement() {
  const { pipelineId, pipelines } = usePipelineCtx();
  const [tags, setTags] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [deleteState, setDeleteState] = useState(null);
  // deleteState = { tag, total_deals_count, active_deals_count } | null

  const load = () => {
    if (!pipelineId) return;
    api
      .get("/tags", { params: { pipeline_id: pipelineId } })
      .then(({ data }) => setTags(data));
  };
  useEffect(() => {
    load();
  }, [pipelineId]); // eslint-disable-line

  const onDeleteClick = async (t) => {
    // Fetch usage count first (bug #2)
    try {
      const { data } = await api.get(`/tags/${t.tag_id}/usage`);
      setDeleteState({
        tag: t,
        total_deals_count: data.total_deals_count || 0,
        active_deals_count: data.active_deals_count || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to check tag usage");
    }
  };

  const confirmDelete = async () => {
    if (!deleteState) return;
    try {
      await api.delete(`/tags/${deleteState.tag.tag_id}`);
      toast.success("Tag deleted");
      setDeleteState(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete tag");
    }
  };

  const currentPipe = pipelines.find((p) => p.pipeline_id === pipelineId);

  return (
    <div className="space-y-4">
      <div className="cw-card p-5 flex items-center justify-between">
        <div className="text-xs text-[var(--cw-muted)]">
          Tags for <strong>{currentPipe?.pipeline_name || "…"}</strong>. Use the
          sidebar switcher to change context.
        </div>
        <button
          className="cw-btn-primary flex items-center gap-2"
          onClick={() => setShowCreate(true)}
          data-testid="tag-create-btn"
        >
          <Plus size={14} /> Create Tag
        </button>
      </div>

      <div className="cw-card overflow-hidden">
        <table className="w-full text-sm" data-testid="tags-table">
          <thead className="bg-black/5">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Color</th>
              <th className="text-left px-4 py-2 font-medium">Description</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tags.map((t) => (
              <tr
                key={t.tag_id}
                className="border-t"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid={`tag-row-${t.tag_name}`}
              >
                <td className="px-4 py-2">
                  <span
                    className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: t.tag_color, color: "#fff" }}
                  >
                    <TagIcon size={11} /> {t.tag_name}
                  </span>
                </td>
                <td className="px-4 py-2 text-[var(--cw-muted)] font-mono text-xs">
                  {t.tag_color}
                </td>
                <td className="px-4 py-2 text-[var(--cw-muted)]">
                  {t.description || "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    className="p-1.5 rounded hover:bg-black/5 mr-1"
                    onClick={() => setEditTag(t)}
                    title="Edit"
                    data-testid={`tag-edit-${t.tag_name}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    className="p-1.5 rounded hover:bg-red-50"
                    onClick={() => onDeleteClick(t)}
                    title="Delete"
                    data-testid={`tag-delete-${t.tag_name}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {tags.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="text-center py-6 text-sm text-[var(--cw-muted)]"
                >
                  No tags yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <TagFormModal
          pipelineId={pipelineId}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}
      {editTag && (
        <TagFormModal
          key={editTag.tag_id}
          pipelineId={pipelineId}
          editingTag={editTag}
          onClose={() => setEditTag(null)}
          onSaved={() => {
            setEditTag(null);
            load();
          }}
        />
      )}
      {deleteState && (
        <DeleteTagModal
          state={deleteState}
          onCancel={() => setDeleteState(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

function DeleteTagModal({ state, onCancel, onConfirm }) {
  const { tag, total_deals_count } = state;
  const hasDeals = total_deals_count > 0;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="tag-delete-modal"
    >
      <div
        className="cw-card p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="cw-h3 mb-3 flex items-center gap-2">
          {hasDeals && <AlertTriangle size={18} className="text-[#B02B2B]" />}
          Delete tag?
        </h3>
        {hasDeals ? (
          <p className="text-sm text-[var(--cw-muted)]">
            This tag is associated with <strong>{total_deals_count} deal
            {total_deals_count === 1 ? "" : "s"}</strong>. Deleting it will
            remove the tag from those deals. Do you want to continue?
          </p>
        ) : (
          <p className="text-sm text-[var(--cw-muted)]">
            Delete tag <strong>“{tag.tag_name}”</strong>? This can’t be undone.
          </p>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button
            className="cw-btn-secondary"
            onClick={onCancel}
            data-testid="tag-delete-cancel"
          >
            No
          </button>
          <button
            className="text-sm px-3 py-1.5 rounded"
            style={{ background: "var(--cw-error)", color: "white" }}
            onClick={onConfirm}
            data-testid="tag-delete-confirm"
          >
            {hasDeals ? "Yes, delete" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TagFormModal({ pipelineId, editingTag, onClose, onSaved }) {
  const isEdit = !!editingTag;
  const [name, setName] = useState(editingTag?.tag_name || "");
  const [color, setColor] = useState(editingTag?.tag_color || "#282A74");
  const [description, setDescription] = useState(editingTag?.description || "");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) {
      setError("Name required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        tag_name: name.trim(),
        tag_color: color,
        description: description || null,
      };
      if (isEdit) {
        await api.patch(`/tags/${editingTag.tag_id}`, payload);
        toast.success("Tag updated");
      } else {
        await api.post("/tags", { pipeline_id: pipelineId, ...payload });
        toast.success("Tag created");
      }
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="tag-create-modal"
    >
      <div className="cw-card p-6 w-full max-w-md">
        <h3 className="cw-h3 mb-4">{isEdit ? "Edit tag" : "Create tag"}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="cw-caption">Name</div>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              className="w-full h-9 px-3 rounded-md border text-sm mt-1"
              style={{ borderColor: error ? "var(--cw-error)" : "var(--cw-border)" }}
              data-testid="tag-create-name"
              autoFocus
            />
          </div>
          <div>
            <div className="cw-caption">Color</div>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-full h-9 rounded-md border mt-1"
              data-testid="tag-create-color"
            />
          </div>
        </div>
        {error && (
          <div
            className="mt-2 text-xs px-2 py-1.5 rounded"
            style={{ background: "rgba(215,60,60,0.08)", color: "#B02B2B" }}
            data-testid="tag-create-error"
          >
            {error}
          </div>
        )}
        <div className="mt-3">
          <div className="cw-caption">Description</div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded-md border text-sm mt-1"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="tag-create-description"
          />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="cw-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="cw-btn-primary"
            onClick={submit}
            disabled={saving}
            data-testid="tag-create-submit"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
